// OSIS Cloud Storage Dashboard
import React, { useState, useEffect } from 'react';
import {
  Cloud,
  Database,
  HardDrive,
  Upload,
  Download,
  Server,
  Settings,
  FileText,
  Image,
  Video,
  Archive,
  Trash2,
  Eye,
  Share2,
  Lock,
  Unlock,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Zap,
  BarChart3
} from 'lucide-react';

const CloudDashboard = () => {
  const [storageData, setStorageData] = useState({
    used: 23.7,
    total: 100,
    files: 1247,
    folders: 89,
    recentUploads: 15
  });

  const [recentFiles, setRecentFiles] = useState([
    {
      id: 1,
      name: 'Proiect_Casa_Ionescu.pdf',
      type: 'pdf',
      size: '2.3 MB',
      modified: '2025-01-15 14:30',
      shared: true,
      encrypted: true
    },
    {
      id: 2,
      name: 'Masuratori_Apartament_Cluj.xlsx',
      type: 'xlsx',
      size: '1.8 MB',
      modified: '2025-01-15 13:15',
      shared: false,
      encrypted: true
    },
    {
      id: 3,
      name: 'Fotografie_Montaj_Brasov.jpg',
      type: 'image',
      size: '5.2 MB',
      modified: '2025-01-15 11:45',
      shared: true,
      encrypted: false
    },
    {
      id: 4,
      name: 'Video_Prezentare_OSIS.mp4',
      type: 'video',
      size: '45.7 MB',
      modified: '2025-01-14 16:20',
      shared: true,
      encrypted: false
    }
  ]);

  const [backupStatus, setBackupStatus] = useState({
    lastBackup: '2025-01-15 12:00',
    status: 'success',
    nextScheduled: '2025-01-16 00:00'
  });

  const getFileIcon = (type) => {
    switch (type) {
      case 'pdf':
      case 'doc':
      case 'docx':
        return <FileText className="w-5 h-5 text-red-600" />;
      case 'jpg':
      case 'png':
      case 'gif':
      case 'image':
        return <Image className="w-5 h-5 text-green-600" />;
      case 'mp4':
      case 'avi':
      case 'video':
        return <Video className="w-5 h-5 text-purple-600" />;
      case 'zip':
      case 'rar':
        return <Archive className="w-5 h-5 text-orange-600" />;
      default:
        return <FileText className="w-5 h-5 text-gray-600" />;
    }
  };

  const usagePercentage = (storageData.used / storageData.total) * 100;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center mb-4">
            <Cloud className="w-8 h-8 text-cyan-600 mr-3" />
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                OSIS Cloud Storage
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mt-1">
                Gestionează fișierele și documentele cu Amazon S3
              </p>
            </div>
          </div>
        </div>

        {/* Storage Overview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center">
              <div className="flex items-center justify-center w-12 h-12 bg-cyan-100 dark:bg-cyan-900 rounded-xl">
                <Database className="w-6 h-6 text-cyan-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Stocare Utilizată</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {storageData.used} GB
                </p>
                <p className="text-xs text-gray-400">din {storageData.total} GB</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center">
              <div className="flex items-center justify-center w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-xl">
                <FileText className="w-6 h-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Fișiere</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {storageData.files.toLocaleString()}
                </p>
                <p className="text-xs text-green-600">+{storageData.recentUploads} astăzi</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center">
              <div className="flex items-center justify-center w-12 h-12 bg-purple-100 dark:bg-purple-900 rounded-xl">
                <HardDrive className="w-6 h-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Foldere</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {storageData.folders}
                </p>
                <p className="text-xs text-gray-400">organizate</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center">
              <div className="flex items-center justify-center w-12 h-12 bg-green-100 dark:bg-green-900 rounded-xl">
                <Server className="w-6 h-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Status Backup</p>
                <div className="flex items-center">
                  <CheckCircle className="w-4 h-4 text-green-600 mr-1" />
                  <p className="text-sm font-semibold text-green-600">Activ</p>
                </div>
                <p className="text-xs text-gray-400">{backupStatus.lastBackup}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Storage Usage Chart */}
          <div className="lg:col-span-2">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700 p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Utilizare Stocare
                </h3>
                <button className="flex items-center px-3 py-1 bg-cyan-50 dark:bg-cyan-900 text-cyan-700 dark:text-cyan-300 rounded-lg text-sm font-medium hover:bg-cyan-100 dark:hover:bg-cyan-800">
                  <RefreshCw className="w-4 h-4 mr-1" />
                  Refresh
                </button>
              </div>

              {/* Storage Bar */}
              <div className="mb-6">
                <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mb-2">
                  <span>{storageData.used} GB utilizate</span>
                  <span>{storageData.total - storageData.used} GB disponibile</span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-4">
                  <div 
                    className={`h-4 rounded-full transition-all duration-300 ${
                      usagePercentage > 80 ? 'bg-red-500' : 
                      usagePercentage > 60 ? 'bg-yellow-500' : 'bg-cyan-500'
                    }`}
                    style={{ width: `${usagePercentage}%` }}
                  />
                </div>
                <div className="text-right text-xs text-gray-500 mt-1">
                  {usagePercentage.toFixed(1)}% utilizat
                </div>
              </div>

              {/* Quick Actions */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <button className="flex flex-col items-center p-4 bg-gray-50 dark:bg-gray-700 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors">
                  <Upload className="w-6 h-6 text-blue-600 mb-2" />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Upload</span>
                </button>
                <button className="flex flex-col items-center p-4 bg-gray-50 dark:bg-gray-700 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors">
                  <Download className="w-6 h-6 text-green-600 mb-2" />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Download</span>
                </button>
                <button className="flex flex-col items-center p-4 bg-gray-50 dark:bg-gray-700 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors">
                  <Share2 className="w-6 h-6 text-purple-600 mb-2" />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Partajare</span>
                </button>
                <button className="flex flex-col items-center p-4 bg-gray-50 dark:bg-gray-700 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors">
                  <Settings className="w-6 h-6 text-gray-600 mb-2" />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Setări</span>
                </button>
              </div>
            </div>
          </div>

          {/* Recent Files */}
          <div>
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Fișiere Recente
                </h3>
                <button className="text-cyan-600 hover:text-cyan-700 text-sm font-medium">
                  Vezi toate
                </button>
              </div>

              <div className="space-y-3">
                {recentFiles.map((file) => (
                  <div key={file.id} className="flex items-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors">
                    <div className="flex-shrink-0 mr-3">
                      {getFileIcon(file.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {file.name}
                      </p>
                      <div className="flex items-center text-xs text-gray-500 dark:text-gray-400 mt-1">
                        <span>{file.size}</span>
                        <span className="mx-1">•</span>
                        <span>{file.modified}</span>
                      </div>
                    </div>
                    <div className="flex items-center space-x-1">
                      {file.shared && (
                        <Share2 className="w-4 h-4 text-blue-600" title="Partajat" />
                      )}
                      {file.encrypted ? (
                        <Lock className="w-4 h-4 text-green-600" title="Criptat" />
                      ) : (
                        <Unlock className="w-4 h-4 text-gray-400" title="Nu este criptat" />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Backup Status */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700 p-6 mt-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Status Backup
                </h3>
                <div className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                  <span className="text-sm text-green-600 font-medium">Online</span>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Ultimul backup:</span>
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    {backupStatus.lastBackup}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Următorul backup:</span>
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    {backupStatus.nextScheduled}
                  </span>
                </div>
                <button className="w-full bg-cyan-600 hover:bg-cyan-700 text-white font-medium py-2 px-4 rounded-lg transition-colors">
                  Pornește Backup Manual
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Amazon S3 Status */}
        <div className="mt-8">
          <div className="bg-gradient-to-r from-orange-50 to-yellow-50 dark:from-orange-900/20 dark:to-yellow-900/20 rounded-xl border border-orange-200 dark:border-orange-800 p-6">
            <div className="flex items-center">
              <div className="flex items-center justify-center w-12 h-12 bg-orange-100 dark:bg-orange-900 rounded-xl mr-4">
                <Zap className="w-6 h-6 text-orange-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  Conectat la Amazon S3
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  Bucket: <span className="font-medium">osis-storage-ro</span> • 
                  Regiune: <span className="font-medium">eu-central-1</span> • 
                  Status: <span className="text-green-600 font-medium">Conectat</span>
                </p>
              </div>
              <button className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white font-medium rounded-lg transition-colors">
                Configurează S3
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CloudDashboard;
